import React, { useEffect, useState } from "react";
import { Heading, Subheading } from "../../../../../design/heading";
import { Button } from "../../../../../design/button";
import { useUser } from "../../../../../contexts/UserContext";
import { Text } from "../../../../../design/text";
import { Input } from "../../../../../design/input";
import {
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from "../../../../../design/fieldset";
import { useAuth } from "../../../../../contexts/AuthContext";
import { useTenant } from "../../../../../contexts/TenantContext";
import { Intake } from "../../../../../models/Intake";
import OnboardingContainer from "../../../components/OnboardingContainer";

interface Errors {
  firstName?: string;
  lastName?: string;
}

const OnboardingMeStep: React.FC<{
  formData: Omit<
    Intake,
    "_id" | "createdAt" | "updatedAt" | "createdBy" | "updatedBy" | "status"
  >;
  setFormData: React.Dispatch<
    React.SetStateAction<
      Omit<
        Intake,
        "_id" | "createdAt" | "updatedAt" | "createdBy" | "updatedBy" | "status"
      >
    >
  >;
  onStepComplete: () => void;
}> = ({ formData, setFormData, onStepComplete }) => {
  const { user } = useAuth();
  const { tenant } = useTenant();
  const { currentUser, updateUser } = useUser();
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Errors>({});

  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [dateOfBirth, setDateOfBirth] = useState<Date | null>(null);

  useEffect(() => {
    if (currentUser) {
      setFirstName(currentUser.firstName || "");
      setLastName(currentUser.lastName || "");
    }
  }, [currentUser]);

  const handleSubmit = async () => {
    if (!user || !firstName || !lastName) return;

    setIsSaving(true);
    try {
      await updateUser(user!.uid, null, { firstName, lastName });
      setFormData((prev) => ({
        ...prev,
        me: {
          ...prev.me,
          name: `${firstName} ${lastName}`,
          dateOfBirth,
        },
      }));
      onStepComplete();
    } catch (error) {
      console.error("Failed to update user:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <OnboardingContainer title={"Your Information"}>
      <p className="mb-6 text-gray-700">
        Please provide some information about yourself. This will help us
        understand your situation better.
      </p>
      <form onSubmit={handleSubmit} className="space-y-6">
        <Fieldset>
          <FieldGroup>
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
              <Field>
                <Label>Your First Name</Label>
                <Input
                  type="text"
                  value={firstName}
                  placeholder="First Name"
                  onChange={(e) => setFirstName(e.target.value)}
                />
                {errors.firstName && (
                  <ErrorMessage>{errors.firstName}</ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>Your Last Name</Label>
                <Input
                  type="text"
                  value={lastName}
                  placeholder="Last Name"
                  onChange={(e) => setLastName(e.target.value)}
                />
                {errors.lastName && (
                  <ErrorMessage>{errors.lastName}</ErrorMessage>
                )}
              </Field>
            </div>
          </FieldGroup>
          <Field>
            <Label>Your Date of Birth:</Label>
            <Input
              type="date"
              placeholder="Your Spouse's Date of Birth"
              className="w-full"
              value={dateOfBirth ? dateOfBirth.toISOString().split("T")[0] : ""}
              onChange={(e) => {
                const date = new Date(e.target.value);
                if (!isNaN(date.getTime())) {
                  setDateOfBirth(date);
                }
              }}
              required
            />
          </Field>
        </Fieldset>
      </form>

      <div className="flex items-end w-full mt-8 space-x-4">
        <Button
          className="w-full"
          onClick={handleSubmit}
          disabled={!firstName || !lastName || !dateOfBirth || isSaving}
        >
          {isSaving ? "Saving..." : "Next"}
        </Button>
      </div>
    </OnboardingContainer>
  );
};

export default OnboardingMeStep;
